// src/store/slices/achievementsSlice.ts
// 🏆 Управление достижениями

import type { StateCreator } from 'zustand';
import {
  ACHIEVEMENTS,
  ACHIEVEMENTS_VERSION,
  type Achievement,
  checkPerfectWeek,
  checkConsecutivePerfectWeeks,
  checkComebackCount,
  checkCompleteCourse,
} from '../../data/achievementsData';
import { CloudStorage } from '../../utils/cloudStorage';
import { formatInTimeZone } from 'date-fns-tz';

// ========== TIMEZONE HELPER ==========

/**
 * ✅ FIX: Получает дату в формате YYYY-MM-DD с учетом user.timezone
 * Использует date-fns-tz для корректной работы в любом часовом поясе
 */
function getDateInUserTimezone(date: Date, timezone: string): string {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
}

// ========== ЕДИНЫЙ ФОРМАТ ХРАНЕНИЯ ==========

// ✅ FIX КРИТИЧНО: Telegram CloudStorage не поддерживает ':' в ключах
// Было: 'achievements:v3' → STORAGE_KEY_INVALID (бесконечный рендер!)
// Стало: 'achievements_v3' (только [A-Za-z0-9_-])
const ACHIEVEMENTS_KEY = 'achievements_v3';
const LEGACY_KEY_WITH_COLON = 'achievements:v3'; // Для миграции
const MIGRATION_FLAG_KEY = 'achievements_migration_done'; // Флаг что миграция завершена

interface AchievementsStorage {
  items: Achievement[];
  toastsShown: string[]; // ID достижений, для которых уже показывали тост
  updated: number; // Date.now()
  version: number; // ACHIEVEMENTS_VERSION
  resetTimestamp?: number; // ✅ КРИТИЧНО: Timestamp последнего reset - защита от кросс-девайс конфликтов
}

// ========== ТРАНЗАКЦИОННОЕ СОХРАНЕНИЕ (read-modify-write) ==========

async function saveAchievementsTransactional(
  achievements: Achievement[],
  toastsShown: string[] = [],
  resetTimestamp?: number // ✅ КРИТИЧНО: Передаём resetTimestamp при сбросе
): Promise<void> {
  const maxRetries = 2;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // ✅ Read: читаем текущее состояние
      const currentRaw = await CloudStorage.getItem(ACHIEVEMENTS_KEY);
      const current: AchievementsStorage | null = currentRaw ? JSON.parse(currentRaw) : null;

      // ✅ КРИТИЧНО БАГ FIX: Усиленная защита от кросс-девайс race conditions после reset
      // Если в CloudStorage есть БОЛЕЕ НОВЫЙ reset, игнорируем локальные изменения
      // Защита увеличена с 1 до 2 минут для надежности (CloudStorage retry может занять до 7 сек)
      const RESET_PROTECTION_TIME = 120000; // 2 минуты
      if (current?.resetTimestamp && !resetTimestamp) {
        const timeSinceReset = Date.now() - current.resetTimestamp;
        if (timeSinceReset < RESET_PROTECTION_TIME) {
          // Reset был недавно - НЕ перезаписываем пустое состояние старыми данными
          if (process.env.NODE_ENV === 'development') {
            console.log(`⚠️ Обнаружен недавний reset в CloudStorage (${Math.round(timeSinceReset / 1000)}с назад), игнорируем локальные изменения`);
          }
          return; // Не сохраняем
        }
      }

      // ✅ Modify: мержим с текущими данными (last-write-wins для каждого достижения)
      const mergedItems = current?.items
        ? mergeAchievements(current.items, achievements)
        : achievements;

      const mergedToastsShown = current?.toastsShown
        ? Array.from(new Set([...current.toastsShown, ...toastsShown]))
        : toastsShown;

      // ✅ Write: сохраняем с новым timestamp
      const newData: AchievementsStorage = {
        items: mergedItems,
        toastsShown: mergedToastsShown,
        updated: Date.now(),
        version: ACHIEVEMENTS_VERSION,
        resetTimestamp: resetTimestamp || current?.resetTimestamp, // Сохраняем resetTimestamp
      };

      await CloudStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(newData));
      if (process.env.NODE_ENV === 'development') {
        console.log('🔒 Достижения сохранены транзакционно в CloudStorage (попытка', attempt + 1, ')');
      }
      return;
    } catch (error) {
      console.error(`❌ Ошибка сохранения достижений (попытка ${attempt + 1}):`, error);

      if (attempt === maxRetries - 1) {
        // Последняя попытка - fallback на localStorage
        try {
          const data: AchievementsStorage = {
            items: achievements,
            toastsShown,
            updated: Date.now(),
            version: ACHIEVEMENTS_VERSION,
          };
          localStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(data));
          console.warn('⚠️ Fallback на localStorage');
        } catch (fallbackError) {
          console.error('❌ Ошибка fallback:', fallbackError);
        }
      }
    }
  }
}

/**
 * ✅ MERGE СТРАТЕГИЯ: приоритет более свежему unlockedAt
 * Если оба открыты - берём тот, который открыт позже
 * Если один открыт - берём открытый
 */
function mergeAchievements(remote: Achievement[], local: Achievement[]): Achievement[] {
  const mergedMap = new Map<string, Achievement>();

  // Сначала добавляем remote
  for (const achievement of remote) {
    mergedMap.set(achievement.id, achievement);
  }

  // Затем мержим с local (приоритет более свежему)
  for (const localAch of local) {
    const remoteAch = mergedMap.get(localAch.id);

    if (!remoteAch) {
      // Новое достижение только в local
      mergedMap.set(localAch.id, localAch);
      continue;
    }

    // ✅ FIX: Оба существуют - выбираем более свежее с корректной проверкой дат
    if (localAch.unlocked && remoteAch.unlocked) {
      // Оба открыты - берём с более поздней датой
      // Если unlockedAt отсутствует, считаем дату = 0 (самая старая)
      const localDate = localAch.unlockedAt ? new Date(localAch.unlockedAt).getTime() : 0;
      const remoteDate = remoteAch.unlockedAt ? new Date(remoteAch.unlockedAt).getTime() : 0;
      mergedMap.set(localAch.id, localDate >= remoteDate ? localAch : remoteAch);
    } else if (localAch.unlocked) {
      // Только local открыто
      mergedMap.set(localAch.id, localAch);
    } else if (remoteAch.unlocked) {
      // Только remote открыто
      mergedMap.set(localAch.id, remoteAch);
    } else {
      // Оба закрыты - берём с большим progress
      const localProgress = localAch.progress || 0;
      const remoteProgress = remoteAch.progress || 0;
      mergedMap.set(localAch.id, localProgress >= remoteProgress ? localAch : remoteAch);
    }
  }

  return Array.from(mergedMap.values());
}

/**
 * ✅ ГИДРАТАЦИЯ: загружает достижения из CloudStorage
 * Всегда возвращает смерженное состояние с каталогом
 */
async function loadAchievements(): Promise<{ achievements: Achievement[]; toastsShown: string[] }> {
  try {
    const dataRaw = await CloudStorage.getItem(ACHIEVEMENTS_KEY);

    // Если данных нет — проверяем старые ключи для миграции
    if (!dataRaw) {
      // ✅ ОПТИМИЗАЦИЯ: Проверяем флаг миграции чтобы не делать лишние CloudStorage вызовы
      const migrationDone = localStorage.getItem(MIGRATION_FLAG_KEY);

      if (migrationDone !== 'true') {
        if (process.env.NODE_ENV === 'development') {
          console.log('ℹ️ Нет данных в achievements_v3, проверяем старые ключи...');
        }

        // ✅ МИГРАЦИЯ КРИТИЧНО: Сначала проверяем старый ключ с двоеточием (он вызывал STORAGE_KEY_INVALID)
        try {
        // Пытаемся загрузить из localStorage (куда fallback сохранял из-за STORAGE_KEY_INVALID)
        const legacyRaw = localStorage.getItem(LEGACY_KEY_WITH_COLON);
        if (legacyRaw) {
          const legacyData: AchievementsStorage = JSON.parse(legacyRaw);

          // ✅ FIX: Проверяем что данные валидны
          if (!legacyData || !legacyData.items || !Array.isArray(legacyData.items)) {
            throw new Error('Invalid legacy data structure');
          }

          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 Найден legacy ключ achievements:v3 в localStorage, мигрируем...');
          }

          // Мигрируем в новый формат
          const savedMap = new Map(legacyData.items.map((a) => [a.id, a]));
          const migratedAchievements = ACHIEVEMENTS.map((catalogItem) => {
            const saved = savedMap.get(catalogItem.id);
            return saved
              ? {
                  ...catalogItem,
                  unlocked: saved.unlocked,
                  unlockedAt: saved.unlockedAt,
                  progress: saved.progress || 0,
                }
              : { ...catalogItem, unlocked: false, progress: 0 };
          });

          // Сохраняем в новом формате с валидным ключом
          const newData: AchievementsStorage = {
            items: migratedAchievements,
            toastsShown: legacyData.toastsShown || [],
            updated: Date.now(),
            version: ACHIEVEMENTS_VERSION,
          };

          await CloudStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(newData));

          // Очищаем legacy ключ из localStorage
          localStorage.removeItem(LEGACY_KEY_WITH_COLON);

          // ✅ ОПТИМИЗАЦИЯ: Устанавливаем флаг что миграция завершена
          localStorage.setItem(MIGRATION_FLAG_KEY, 'true');

          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Миграция achievements:v3 → achievements_v3 завершена');
          }

          return {
            achievements: migratedAchievements,
            toastsShown: legacyData.toastsShown || [],
          };
        }
      } catch (legacyError) {
        console.warn('⚠️ Ошибка миграции legacy ключа:', legacyError);
      }

      // ✅ МИГРАЦИЯ: пытаемся загрузить из старых ключей (pre-v3)
      const oldAchievements = await CloudStorage.getItem('user_achievements');
      const oldHash = await CloudStorage.getItem('achievements_hash');
      const oldVersion = await CloudStorage.getItem('achievements_version');

      if (oldAchievements) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Найдены старые ключи, выполняем миграцию...');
        }

        try {
          const oldData: Achievement[] = JSON.parse(oldAchievements);

          // Merge старых данных с каталогом
          const savedMap = new Map(oldData.map((a) => [a.id, a]));
          const migratedAchievements = ACHIEVEMENTS.map((catalogItem) => {
            const saved = savedMap.get(catalogItem.id);
            return saved
              ? {
                  ...catalogItem,
                  unlocked: saved.unlocked,
                  unlockedAt: saved.unlockedAt,
                  progress: saved.progress || 0,
                }
              : { ...catalogItem, unlocked: false, progress: 0 };
          });

          // Сохраняем в новом формате
          const newData: AchievementsStorage = {
            items: migratedAchievements,
            toastsShown: [], // Старые тосты не мигрируем
            updated: Date.now(),
            version: ACHIEVEMENTS_VERSION,
          };

          await CloudStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(newData));
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Миграция завершена, старые данные сохранены в новом формате');
          }

          // Очищаем старые ключи
          await CloudStorage.removeItem('user_achievements');
          await CloudStorage.removeItem('achievements_hash');
          await CloudStorage.removeItem('achievements_version');

          // ✅ ОПТИМИЗАЦИЯ: Устанавливаем флаг что миграция завершена
          localStorage.setItem(MIGRATION_FLAG_KEY, 'true');

          if (process.env.NODE_ENV === 'development') {
            console.log('🧹 Старые ключи удалены из CloudStorage');
          }

          return {
            achievements: migratedAchievements,
            toastsShown: [],
          };
        } catch (error) {
          console.error('❌ Ошибка миграции:', error);
        }
      }

        // ✅ ОПТИМИЗАЦИЯ: Устанавливаем флаг даже если миграции не было (чтобы не проверять снова)
        localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
      } // Закрываем if (migrationDone !== 'true')

      if (process.env.NODE_ENV === 'development') {
        console.log('ℹ️ Нет сохранённых достижений, используем каталог');
      }
      return {
        achievements: ACHIEVEMENTS.map((a) => ({ ...a, unlocked: false, progress: 0 })),
        toastsShown: [],
      };
    }

    const data: AchievementsStorage = JSON.parse(dataRaw);

    // Проверяем версию каталога
    const versionChanged = data.version !== ACHIEVEMENTS_VERSION;
    if (versionChanged) {
      console.warn(
        `⚠️ Версия каталога изменилась (${data.version} → ${ACHIEVEMENTS_VERSION}). Требуется merge.`
      );
    }

    // Merge: берём каталог как основу, восстанавливаем прогресс из сохранённых
    const savedMap = new Map(data.items.map((a) => [a.id, a]));

    const mergedAchievements = ACHIEVEMENTS.map((catalogItem) => {
      const saved = savedMap.get(catalogItem.id);

      if (saved) {
        // Достижение есть в обоих — сохраняем прогресс
        return {
          ...catalogItem,
          unlocked: saved.unlocked,
          unlockedAt: saved.unlockedAt,
          progress: saved.progress || 0,
        };
      } else {
        // Новое достижение из каталога
        return {
          ...catalogItem,
          unlocked: false,
          progress: 0,
        };
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(
        `✅ Достижения загружены из CloudStorage${versionChanged ? ' (версия обновлена)' : ''}`,
        'updated:', new Date(data.updated).toLocaleString()
      );
    }

    return {
      achievements: mergedAchievements,
      toastsShown: data.toastsShown || [],
    };
  } catch (error) {
    console.error('❌ Ошибка загрузки, fallback на localStorage:', error);

    // Fallback на localStorage
    try {
      const dataRaw = localStorage.getItem(ACHIEVEMENTS_KEY);
      if (!dataRaw) {
        return {
          achievements: ACHIEVEMENTS.map((a) => ({ ...a, unlocked: false, progress: 0 })),
          toastsShown: [],
        };
      }

      const data: AchievementsStorage = JSON.parse(dataRaw);
      const savedMap = new Map(data.items.map((a) => [a.id, a]));

      const mergedAchievements = ACHIEVEMENTS.map((catalogItem) => {
        const saved = savedMap.get(catalogItem.id);
        return saved
          ? {
              ...catalogItem,
              unlocked: saved.unlocked,
              unlockedAt: saved.unlockedAt,
              progress: saved.progress || 0,
            }
          : { ...catalogItem, unlocked: false, progress: 0 };
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Достижения из localStorage (fallback)');
      }
      return {
        achievements: mergedAchievements,
        toastsShown: data.toastsShown || [],
      };
    } catch (fallbackError) {
      console.error('❌ Ошибка fallback:', fallbackError);
      return {
        achievements: ACHIEVEMENTS.map((a) => ({ ...a, unlocked: false, progress: 0 })),
        toastsShown: [],
      };
    }
  }
}

// ========== SLICE STATE ==========

export interface AchievementsSlice {
  // State
  newAchievements: Achievement[];
  currentStage: 1 | 2 | 3;
  toastsShown: string[]; // ✅ НОВОЕ: ID тостов, которые уже показывали

  // Runtime (не сохраняем в persist)
  isCheckingAchievements: boolean;
  achievementsCache: Achievement[] | null;
  lastAchievementsCheck: number | null;

  // Actions
  checkAndUnlockAchievements: (forceReload?: boolean) => Promise<void>;
  clearNewAchievements: () => void;
  removeShownAchievement: (achievementId: string) => Promise<void>; // ✅ ИСПРАВЛЕНО: async
  setStageFromBackend: (stage: number) => void;
  checkStageUpgrade: () => void;
  resetAchievements: () => Promise<void>; // ✅ ИСПРАВЛЕНО: async
}

// ========== INITIAL STATE ==========

const initialAchievementsState = {
  newAchievements: [],
  currentStage: 1 as 1 | 2 | 3,
  toastsShown: [], // ✅ НОВОЕ
  isCheckingAchievements: false,
  achievementsCache: null,
  lastAchievementsCheck: null,
};

// ========== HELPER ==========

const daysBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// ========== SLICE CREATOR ==========

export const createAchievementsSlice: StateCreator<
  AchievementsSlice,
  [],
  [],
  AchievementsSlice
> = (set, get) => ({
  ...initialAchievementsState,

  checkAndUnlockAchievements: async (forceReload = false) => {
    const now = Date.now();
    const state = get();

    // Защита от ре-энтри
    if (state.isCheckingAchievements) return;

    // Троттлинг: не чаще раза в 2 сек (если не forceReload)
    if (!forceReload && state.lastAchievementsCheck && now - state.lastAchievementsCheck < 2000) {
      return;
    }

    set({ isCheckingAchievements: true });

    try {
      // ✅ FIX: Memory optimization - TTL для кеша (5 минут)
      const CACHE_TTL = 5 * 60 * 1000; // 5 минут
      const cacheExpired =
        state.lastAchievementsCheck && now - state.lastAchievementsCheck > CACHE_TTL;

      if (cacheExpired) {
        set({ achievementsCache: null });
        if (process.env.NODE_ENV === 'development') {
          console.log('🧹 Кеш достижений очищен (TTL истек)');
        }
      }

      // ✅ КРИТИЧНО: если forceReload - всегда загружаем из CloudStorage
      let achievements: Achievement[];
      let toastsShown: string[];

      if (forceReload || !state.achievementsCache || cacheExpired) {
        const loaded = await loadAchievements();
        achievements = loaded.achievements;

        // ✅ КРИТИЧНО: Мержим toastsShown вместо перезаписи!
        // Проблема: CloudStorage синхронизируется НЕ мгновенно
        // Если пользователь закрыл тост локально, а потом произошёл forceReload,
        // CloudStorage может вернуть СТАРЫЙ toastsShown (без закрытого тоста)
        // Решение: объединяем локальный и remote toastsShown
        toastsShown = Array.from(new Set([...state.toastsShown, ...loaded.toastsShown]));

        set({ achievementsCache: achievements, toastsShown });
      } else {
        achievements = state.achievementsCache;
        toastsShown = state.toastsShown;
      }

      // ✅ FIX: Получаем данные из других slices с безопасной типизацией
      // Вместо опасного `as any` используем интерфейс для нужных полей
      const fullState = get() as unknown as {
        applications?: Array<{ date: string; marked: boolean }>;
        plannedDates?: string[];
        currentStreak?: number;
        shareCount?: number;
        referralCount?: number;
        feedbackCount?: number;
        currentStage?: 1 | 2 | 3;
        user?: { timezone?: string }; // ✅ FIX: добавляем user.timezone
      };
      const applications = fullState.applications || [];
      const plannedDates = fullState.plannedDates || [];
      const currentStreak = fullState.currentStreak || 0;
      const shareCount = fullState.shareCount || 0;
      const referralCount = fullState.referralCount || 0;
      const feedbackCount = fullState.feedbackCount || 0;
      const currentStage = fullState.currentStage || 1;
      const userTimezone = fullState.user?.timezone || 'UTC'; // ✅ FIX: используем user.timezone

      const newlyUnlocked: Achievement[] =[];

      const updatedAchievements = achievements.map((achievement) => {
        if (!achievement.trigger || !achievement.trigger.type) {
          return achievement;
        }

        // ✅ FIX: Всегда пересчитываем прогресс, даже для открытых достижений
        // Это исправляет баг: после сброса анкеты достижения должны закрыться
        let shouldUnlock = false;
        let currentProgress = 0;

        switch (achievement.trigger.type) {
          case 'applications': {
            const completedCount = applications.filter((a: any) => a.marked).length;
            const target = achievement.trigger.target || 0;
            currentProgress = completedCount;
            shouldUnlock = completedCount >= target;
            break;
          }
          case 'streak': {
            const target = achievement.trigger.target || 0;
            currentProgress = currentStreak;
            shouldUnlock = currentStreak >= target;
            break;
          }
          case 'perfect_week': {
            const completedCount = applications.filter((a: any) => a.marked).length;
            if (completedCount < 3) {
              shouldUnlock = false;
              currentProgress = 0;
            } else {
              shouldUnlock = checkPerfectWeek(plannedDates, applications, userTimezone); // ✅ FIX: user timezone
              currentProgress = shouldUnlock ? 1 : 0;
            }
            break;
          }
          case 'perfect_weeks_count': {
            // ✅ НОВОЕ: подсчёт последовательных идеальных недель
            const target = achievement.trigger.target || 0;
            shouldUnlock = checkConsecutivePerfectWeeks(plannedDates, applications, target, userTimezone); // ✅ FIX: user timezone

            // Для progress считаем фактическое количество идеальных недель
            // (упрощённый подсчёт без полного дублирования логики)
            const weekMap = new Map<string, { planned: number; marked: number }>();
            for (const date of plannedDates) {
              const d = new Date(date);
              const weekKey = `${d.getFullYear()}-W${Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7)}`;
              if (!weekMap.has(weekKey)) weekMap.set(weekKey, { planned: 0, marked: 0 });
              weekMap.get(weekKey)!.planned++;
              if (applications.some((a: any) => a.date === date && a.marked)) {
                weekMap.get(weekKey)!.marked++;
              }
            }

            let consecutivePerfect = 0;
            const sortedWeeks = Array.from(weekMap.values()).reverse();
            for (const week of sortedWeeks) {
              if (week.planned >= 3 && week.planned === week.marked) {
                consecutivePerfect++;
              } else if (week.marked > 0) break;
            }
            currentProgress = consecutivePerfect;
            break;
          }
          case 'comeback_count': {
            // ✅ НОВОЕ: подсчёт камбэков (возвращений после пропусков)
            const target = achievement.trigger.target || 0;
            shouldUnlock = checkComebackCount(plannedDates, applications, target, userTimezone); // ✅ FIX: user timezone

            // Для progress считаем фактическое количество камбэков
            const today = getDateInUserTimezone(new Date(), userTimezone); // ✅ FIX: user timezone
            const sortedPlanned = [...plannedDates].filter((d) => d <= today).sort();
            let comebacks = 0;
            let hadMiss = false;

            for (const date of sortedPlanned) {
              const isMarked = applications.some((a: any) => a.date === date && a.marked);
              if (!isMarked) {
                hadMiss = true;
              } else if (hadMiss) {
                comebacks++;
                hadMiss = false;
              }
            }
            currentProgress = comebacks;
            break;
          }
          case 'share': {
            currentProgress = shareCount;
            shouldUnlock = shareCount >= 1;
            break;
          }
          case 'referral': {
            currentProgress = referralCount;
            shouldUnlock = referralCount >= 1;
            break;
          }
          case 'feedback': {
            currentProgress = feedbackCount;
            shouldUnlock = feedbackCount >= 1;
            break;
          }
          case 'stage_upgrade': {
            const target = achievement.trigger.target || 1;
            currentProgress = currentStage;
            shouldUnlock = currentStage >= target;
            break;
          }
          case 'complete_course': {
            // ✅ НОВОЕ: "Легенда ухода" - все запланированные даты отмечены
            shouldUnlock = checkCompleteCourse(plannedDates, applications);
            // Progress = количество отмеченных дат из всех запланированных
            const markedCount = plannedDates.filter((date: string) =>
              applications.some((app: any) => app.date === date && app.marked)
            ).length;
            currentProgress = markedCount;
            break;
          }
        }

        // ✅ FIX: Закрываем достижение если оно было открыто, но условия больше не выполняются
        // Это критично для корректного сброса после удаления анкеты
        if (achievement.unlocked && !shouldUnlock) {
          return {
            ...achievement,
            unlocked: false,
            unlockedAt: undefined,
            progress: currentProgress,
          };
        }

        if (shouldUnlock) {
          const unlockedAchievement = {
            ...achievement,
            unlocked: true,
            unlockedAt: achievement.unlockedAt || new Date().toISOString(), // ✅ Сохраняем оригинальное время
            progress: currentProgress,
          } as Achievement;

          // ✅ КРИТИЧНО: Добавляем в newlyUnlocked ТОЛЬКО если достижение НЕ было открыто ранее!
          // Это предотвращает повторный показ тостов при каждом входе
          if (!achievement.unlocked) {
            newlyUnlocked.push(unlockedAchievement);

            if (process.env.NODE_ENV === 'development') {
              console.log('🏆 Открыто достижение:', unlockedAchievement.name);
            }
          }

          return unlockedAchievement;
        }

        // Даже если не открыто — обновляем progress
        return {
          ...achievement,
          progress: currentProgress,
        };
      });

      // ✅ КРИТИЧНО: Вычисляем updatedToastsShown ДО сохранения в CloudStorage!
      // БАГ БЫЛ: в строке 689 передавался старый toastsShown вместо обновлённого
      const toShow = newlyUnlocked.filter((ach) => !toastsShown.includes(ach.id));
      const updatedToastsShown = toShow.length > 0
        ? [...toastsShown, ...toShow.map((a) => a.id)]
        : toastsShown;

      // Сохраняем только если были изменения
      const changed =
        JSON.stringify(updatedAchievements) !== JSON.stringify(achievements) ||
        updatedToastsShown !== toastsShown;

      if (changed) {
        // ✅ КРИТИЧНО: Передаём updatedToastsShown вместо toastsShown!
        await saveAchievementsTransactional(updatedAchievements, updatedToastsShown);
        set({ achievementsCache: updatedAchievements, toastsShown: updatedToastsShown });
      }

      // ✅ ВАРИАНТ В: дедуп тостов + показ новых достижений
      if (newlyUnlocked.length > 0) {
        if (toShow.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('✨ Новых достижений для показа:', toShow.length);
          }

          // Показываем достижения (toastsShown уже обновлён выше)
          set({ newAchievements: toShow });
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('ℹ️ Все новые достижения уже показаны ранее');
          }
        }
      }
    } finally {
      set({ isCheckingAchievements: false, lastAchievementsCheck: now });
    }
  },

  clearNewAchievements: () => {
    set({ newAchievements: [] });
  },

  removeShownAchievement: async (achievementId: string) => {
    const state = get();
    const filtered = state.newAchievements.filter((a) => a.id !== achievementId);
    const updatedToastsShown = [...state.toastsShown, achievementId];

    set({ newAchievements: filtered, toastsShown: updatedToastsShown });

    // ✅ НОВОЕ: сохраняем toastsShown в CloudStorage
    try {
      if (state.achievementsCache) {
        await saveAchievementsTransactional(state.achievementsCache, updatedToastsShown);
      }
    } catch (error) {
      console.warn('⚠️ Ошибка сохранения toastsShown:', error);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Достижение удалено из очереди и сохранено:', achievementId);
    }
  },

  setStageFromBackend: (stage: number) => {
    if (stage >= 1 && stage <= 3) {
      set({ currentStage: stage as 1 | 2 | 3 });
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Stage установлен из API:', stage);
      }
    }
  },

  checkStageUpgrade: () => {
    // ✅ FIX: Безопасная типизация вместо `as any`
    const fullState = get() as unknown as {
      startDate?: string;
      applications?: Array<{ date: string; marked: boolean }>;
      user?: { timezone?: string };
    };
    const startDate = fullState.startDate;
    const applications = fullState.applications || [];
    const userTimezone = fullState.user?.timezone || 'UTC';

    if (!startDate) return;

    const today = getDateInUserTimezone(new Date(), userTimezone); // ✅ FIX: user timezone
    const daysFromStart = daysBetween(startDate, today);
    const completedCount = applications.filter((a) => a.marked).length;

    let newStage: 1 | 2 | 3 = 1;

    if (daysFromStart >= 30 || completedCount >= 12) {
      newStage = 2;
    }

    if (daysFromStart >= 90 || completedCount >= 36) {
      newStage = 3;
    }

    const currentStage = get().currentStage;
    if (newStage !== currentStage) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`🎉 Переход на этап ${newStage}!`);
      }
      set({ currentStage: newStage });
    }
  },

  resetAchievements: async () => {
    set(initialAchievementsState);

    // ✅ КРИТИЧНО: Очищаем ВСЕ ключи из localStorage включая флаг миграции!
    // (CloudStorage.removeItem не работает с невалидными ключами)
    localStorage.removeItem(LEGACY_KEY_WITH_COLON);
    localStorage.removeItem('user_achievements');
    localStorage.removeItem('achievements_hash');
    localStorage.removeItem('achievements_version');
    localStorage.removeItem(ACHIEVEMENTS_KEY);
    localStorage.removeItem(MIGRATION_FLAG_KEY); // ✅ FIX: Очищаем флаг миграции!

    // ✅ КРИТИЧНО: Вместо удаления сохраняем ПУСТОЕ состояние в CloudStorage с resetTimestamp
    // Это гарантирует что loadAchievements НЕ загрузит старые данные после reset
    // И защищает от кросс-девайс конфликтов через resetTimestamp
    const resetTime = Date.now();
    const emptyState: AchievementsStorage = {
      items: ACHIEVEMENTS.map((a) => ({ ...a, unlocked: false, progress: 0 })),
      toastsShown: [],
      updated: resetTime,
      version: ACHIEVEMENTS_VERSION,
      resetTimestamp: resetTime, // ✅ КРИТИЧНО: Маркер что это состояние после reset
    };

    try {
      await CloudStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(emptyState));
      // Также сохраняем в localStorage как fallback
      localStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(emptyState));
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Сохранено пустое состояние с resetTimestamp:', resetTime);
      }
    } catch (error) {
      console.error('❌ Ошибка сохранения пустого состояния:', error);
      // Fallback: пытаемся хотя бы удалить старые ключи
      const keysToRemove = ['user_achievements', 'achievements_hash', 'achievements_version'];
      for (const key of keysToRemove) {
        try {
          await CloudStorage.removeItem(key);
        } catch (removeError) {
          console.warn(`⚠️ Не удалось удалить ${key}:`, removeError);
        }
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Достижения полностью сброшены с защитой от кросс-девайс конфликтов');
    }
  },
});